import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Button, Select, Row, Input, Col, Checkbox, DatePicker } from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import startCase from 'lodash/startCase';

import GetField from '../Components/GetField';
import styles from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import {
  getVendors,
  clearVendors,
  getRenukaPOIds,
  clearRenukaPOIds,
  getRpoItemsForStoreInward,
  clearRpoItemsForStoreInward,
} from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';

const AntInput = GetField(Input);
const AntSelect = GetField(Select);
const { Option } = Select;
const AntDatePicker = GetField(DatePicker);
const AntCheckbox = GetField(Checkbox);
const AntInputTextArea = GetField(Input.TextArea);

const CreateStoreInward = props => {
  const { classes } = props;
  const history = useHistory();
  const dispatch = useDispatch();

  const { handleSubmit, control, getValues, watch, setValue } = useForm();

  const [confirmLoading, setConfirmLoading] = useState(false);

  const { fields: fabricFields } = useFieldArray({
    control,
    name: 'fabrics',
  });

  const { fields: accessoryFields } = useFieldArray({
    control,
    name: 'accessories',
  });

  const { fields: itemFields } = useFieldArray({
    control,
    name: 'items',
  });

  const { fields: styleFields } = useFieldArray({
    control,
    name: 'styles',
  });

  const vendors = useSelector(state => state.vendors);
  const renukaPOIds = useSelector(state => state.renukaPOIds);
  const rpoItemsForStoreInward = useSelector(
    state => state.rpoItemsForStoreInward
  );

  useEffect(() => {
    dispatch(getRenukaPOIds());
    dispatch(getVendors());

    return () => {
      dispatch(clearRenukaPOIds());
      dispatch(clearVendors());
      dispatch(clearRpoItemsForStoreInward());
    };
  }, []);

  useEffect(async () => {
    try {
      const { data } = await Api.get('/store-inwards/newinwardno');
      setValue('newInwardNo', data);
    } catch (error) {
      errorNotification(error.message);
    }
  }, []);

  useEffect(() => {
    const rpoId = getValues('rpoId');
    if (rpoId) {
      dispatch(getRpoItemsForStoreInward(rpoId));
    }
  }, [watch('rpoId')]);

  useEffect(() => {
    setValue('fabrics', rpoItemsForStoreInward.fabrics);
    setValue('accessories', rpoItemsForStoreInward.accessories);
    setValue('items', rpoItemsForStoreInward.items);
    setValue('styles', rpoItemsForStoreInward.styles);
  }, [rpoItemsForStoreInward]);

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);
      await Api.post('/store-inwards', formValues);
      successNotification('Store inward created');
      history.push('/storeinward/list');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  return (
    <>
      <Breadcrumbs
        moduleName="Store Inward"
        moduleLink="/storeinward/list"
        subModuleName="Create"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <Controller
              name="vendorId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Vendor"
                  allowClear
                  placeholder="Select vendor"
                  {...field}
                  onChange={value => {
                    field.onChange(value);
                    setValue('rpoId', null);
                    setValue('fabrics', []);
                    setValue('accessories', []);
                    setValue('items', []);
                    setValue('styles', []);
                    dispatch(getRenukaPOIds({ vendorId: value }));
                  }}
                >
                  {vendors.map(vendor => (
                    <Option key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="rpoId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="RPO #"
                  allowClear
                  placeholder="Select rpo"
                  {...field}
                >
                  {renukaPOIds.map(rpo => (
                    <Option key={rpo.id} value={rpo.id}>
                      {rpo.rpoNo}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="bpoNo"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="BPO #"
                  disabled
                  placeholder="BPO #"
                  {...{
                    ...field,
                    value: watch(`rpoId`),
                  }}
                >
                  {renukaPOIds.map(rpo => (
                    <Option key={rpo.id} value={rpo.id}>
                      {rpo.bpoNo}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="bpoNo"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Customer"
                  disabled
                  placeholder="Customer"
                  {...{
                    ...field,
                    value: watch(`rpoId`),
                  }}
                >
                  {renukaPOIds.map(rpo => (
                    <Option key={rpo.id} value={rpo.id}>
                      {rpo.customer}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="process"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Process"
                  disabled
                  placeholder="Process"
                  {...{
                    ...field,
                    value: watch(`rpoId`),
                  }}
                >
                  {renukaPOIds.map(rpo => (
                    <Option key={rpo.id} value={rpo.id}>
                      {startCase(rpo.process)}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="newInwardNo"
              control={control}
              render={({ field }) => (
                <AntInput
                  label="Inward Challan #"
                  disabled
                  placeholder="Inward Challan #"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="inwardDate"
              control={control}
              render={({ field }) => (
                <AntDatePicker
                  label="Inward Date"
                  allowClear
                  placeholder="Inward Date"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="isAlter"
              control={control}
              render={({ field }) => (
                <AntCheckbox label="Alter/Reprocess" {...field} />
              )}
            />
          </RightGrid>
        </Row>
        <br />
        {rpoItemsForStoreInward.fabrics.length ? (
          <>
            <div className={classes.previewSubHeading}>Fabrics Table</div>
            <Row>
              <Col className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr>
                      <th className={classes.inputTh}>Product</th>
                      <th className={classes.inputTh}>Fabric</th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Width
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Base Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Print Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        RPO Qty
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Total Rec.
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Balance
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        RPO Rate
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Actual Rate
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Quantity
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Packages
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Invoice #
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Invoice Date
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Chalan #
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Chalan Date
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        HSN Code
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {fabricFields.map((item, index) => (
                      <tr key={item.id}>
                        <td className={classes.inputTd}>{item.product}</td>
                        <td className={classes.inputTd}>{item.fabric}</td>
                        <td className={classes.inputTd}>{item.width}</td>
                        <td className={classes.inputTd}>{item.baseColor}</td>
                        <td className={classes.inputTd}>{item.printColor}</td>
                        <td className={classes.inputTd}>{item.rpoQuantity}</td>
                        <td className={classes.inputTd}>
                          {item.totalReceived}
                        </td>
                        <td className={classes.inputTd}>{item.balance}</td>
                        <td className={classes.inputTd}>{item.rpoRate}</td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`fabrics[${index}].actualRate`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Actual Rate"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`fabrics[${index}].quantity`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Qty"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`fabrics[${index}].packages`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Package" {...field} />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`fabrics[${index}].invoice`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Invoice" {...field} />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`fabrics[${index}].invoiceDate`}
                            control={control}
                            render={({ field }) => (
                              <AntDatePicker
                                placeholder="Invoice Date"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`fabrics[${index}].challan`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Challan" {...field} />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`fabrics[${index}].challanDate`}
                            control={control}
                            render={({ field }) => (
                              <AntDatePicker
                                placeholder="Challan Date"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`fabrics[${index}].hsnNo`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="HSN Code" {...field} />
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
            <br />
          </>
        ) : null}
        {rpoItemsForStoreInward.accessories.length ? (
          <>
            <div className={classes.previewSubHeading}>Accessories Table</div>
            <Row>
              <Col className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr>
                      <th className={classes.inputTh} style={{ width: '12%' }}>
                        Product
                      </th>
                      <th className={classes.inputTh}>Accessory</th>
                      <th className={classes.inputTh} style={{ width: '4%' }}>
                        Unit
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        RPO Qty
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Total Rec.
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Balance
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        RPO Rate
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Actual Rate
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Quantity
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Packages
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Invoice #
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Invoice Date
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Chalan #
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Chalan Date
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        HSN Code
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessoryFields.map((item, index) => (
                      <tr key={item.id}>
                        <td className={classes.inputTd}>{item.product}</td>
                        <td className={classes.inputTd}>{item.accessory}</td>
                        <td className={classes.inputTd}>{item.unit}</td>
                        <td className={classes.inputTd}>{item.rpoQuantity}</td>
                        <td className={classes.inputTd}>
                          {item.totalReceived}
                        </td>
                        <td className={classes.inputTd}>{item.balance}</td>
                        <td className={classes.inputTd}>{item.rpoRate}</td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`accessories[${index}].actualRate`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Actual Rate"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`accessories[${index}].quantity`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Qty"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`accessories[${index}].packages`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Package" {...field} />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`accessories[${index}].invoice`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Invoice" {...field} />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`accessories[${index}].invoiceDate`}
                            control={control}
                            render={({ field }) => (
                              <AntDatePicker
                                placeholder="Invoice Date"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`accessories[${index}].challan`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Challan" {...field} />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`accessories[${index}].challanDate`}
                            control={control}
                            render={({ field }) => (
                              <AntDatePicker
                                placeholder="Challan Date"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`accessories[${index}].hsnNo`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="HSN Code" {...field} />
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
            <br />
          </>
        ) : null}
        {rpoItemsForStoreInward.items.length ? (
          <>
            <div className={classes.previewSubHeading}>Items Table</div>
            <Row>
              <Col className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr>
                      <th className={classes.inputTh}>Item</th>
                      <th className={classes.inputTh} style={{ width: '8%' }}>
                        Shade
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Size
                      </th>
                      <th className={classes.inputTh} style={{ width: '4%' }}>
                        Unit
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        RPO Qty
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Total Rec.
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Balance
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        RPO Rate
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Actual Rate
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Quantity
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Packages
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Invoice #
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Invoice Date
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Chalan #
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Chalan Date
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        HSN Code
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemFields.map((item, index) => (
                      <tr key={item.id}>
                        <td className={classes.inputTd}>{item.name}</td>
                        <td className={classes.inputTd}>{item.shade}</td>
                        <td className={classes.inputTd}>{item.size}</td>
                        <td className={classes.inputTd}>{item.unit}</td>
                        <td className={classes.inputTd}>{item.rpoQuantity}</td>
                        <td className={classes.inputTd}>
                          {item.totalReceived}
                        </td>
                        <td className={classes.inputTd}>{item.balance}</td>
                        <td className={classes.inputTd}>{item.rpoRate}</td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`items[${index}].actualRate`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Actual Rate"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`items[${index}].quantity`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Qty"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`items[${index}].packages`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Package" {...field} />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`items[${index}].invoice`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Invoice" {...field} />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`items[${index}].invoiceDate`}
                            control={control}
                            render={({ field }) => (
                              <AntDatePicker
                                placeholder="Invoice Date"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`items[${index}].challan`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Challan" {...field} />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`items[${index}].challanDate`}
                            control={control}
                            render={({ field }) => (
                              <AntDatePicker
                                placeholder="Challan Date"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`items[${index}].hsnNo`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="HSN Code" {...field} />
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
            <br />
          </>
        ) : null}
        {rpoItemsForStoreInward.styles.length ? (
          <>
            <div className={classes.previewSubHeading}>Products Table</div>
            <Row>
              <Col className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr>
                      <th className={classes.inputTh}>Product</th>
                      <th className={classes.inputTh}>Style</th>
                      <th className={classes.inputTh}>Description</th>
                      <th className={classes.inputTh}>Size</th>
                      <th className={classes.inputTh}>Base Color</th>
                      <th className={classes.inputTh}>Print Color</th>
                      <th className={classes.inputTh}>Embroidery Color</th>
                      <th className={classes.inputTh}>Acid Wash Color</th>
                      <th className={classes.inputTh}>RPO Qty</th>
                      <th className={classes.inputTh}>Total Rec.</th>
                      <th className={classes.inputTh}>Balance</th>
                      <th className={classes.inputTh}>RPO Rate</th>
                      <th className={classes.inputTh}>Actual Rate</th>
                      <th className={classes.inputTh}>Quantity</th>
                      <th className={classes.inputTh}>Packages</th>
                      <th className={classes.inputTh}>Invoice #</th>
                      <th className={classes.inputTh}>Invoice Date</th>
                      <th className={classes.inputTh}>Chalan #</th>
                      <th className={classes.inputTh}>Chalan Date</th>
                      <th className={classes.inputTh}>HSN Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {styleFields.map((item, index) => (
                      <tr key={item.id}>
                        <td className={classes.inputTd}>{item.product}</td>
                        <td className={classes.inputTd}>{item.style}</td>
                        <td className={classes.inputTd}>{item.description}</td>
                        <td className={classes.inputTd}>{item.size}</td>
                        <td className={classes.inputTd}>{item.baseColor}</td>
                        <td className={classes.inputTd}>{item.printColor}</td>
                        <td className={classes.inputTd}>
                          {item.embroideryColor}
                        </td>
                        <td className={classes.inputTd}>
                          {item.acidWashColor}
                        </td>
                        <td className={classes.inputTd}>{item.rpoQuantity}</td>
                        <td className={classes.inputTd}>
                          {item.totalReceived}
                        </td>
                        <td className={classes.inputTd}>{item.balance}</td>
                        <td className={classes.inputTd}>{item.rpoRate}</td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].actualRate`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Actual Rate"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].quantity`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Qty"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].packages`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Package" {...field} />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].invoice`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Invoice" {...field} />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].invoiceDate`}
                            control={control}
                            render={({ field }) => (
                              <AntDatePicker
                                placeholder="Invoice Date"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].challan`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Challan" {...field} />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].challanDate`}
                            control={control}
                            render={({ field }) => (
                              <AntDatePicker
                                placeholder="Challan Date"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].hsnNo`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="HSN Code" {...field} />
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
            <br />
          </>
        ) : null}
        <Row>
          <LeftGrid>
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <AntInputTextArea
                  label="Remark"
                  allowClear
                  placeholder="Remark"
                  {...field}
                />
              )}
            />
          </LeftGrid>
        </Row>
        <Row className={classes.buttonContainer}>
          <Button type="primary" loading={confirmLoading} htmlType="submit">
            Submit
          </Button>
        </Row>
      </form>
    </>
  );
};

CreateStoreInward.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CreateStoreInward);
